import { useCallback, useEffect, useMemo, useState } from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import {
  addToGroup,
  createGroup,
  groupMembership,
  listGroups,
  removeFromGroup,
  type CardGroup,
} from "@/lib/groups";

import type { GroupMenuActions } from "./groupMenu";

/**
 * Confusion groups for a surface that offers グループに追加 (日本-601).
 *
 * **One window event keeps every reader in step.** A group write made from 復習's card menu has to
 * reach a dock or a group page that is also mounted, and the writes are Postgres-only — no
 * `collectionChanged` frame will ever carry them. So each write here dispatches `GROUPS_CHANGED`
 * and every `useGroups` re-reads on it. Cheap: the list is tens of rows.
 */
export const GROUPS_CHANGED = "nihongonator:groups-changed";

export function announceGroupsChanged(): void {
  window.dispatchEvent(new Event(GROUPS_CHANGED));
}

export function useGroupsChanged(listener: () => void): void {
  useEffect(() => {
    window.addEventListener(GROUPS_CHANGED, listener);
    return () => window.removeEventListener(GROUPS_CHANGED, listener);
  }, [listener]);
}

export interface UseGroups {
  groups: CardGroup[];
  /** Note id → the groups it is in, for `noteIds` only. */
  membership: Map<number, string[]>;
  actions: GroupMenuActions;
  error: string | null;
  loaded: boolean;
}

/**
 * The group list, plus membership for the notes a surface is showing.
 *
 * `noteIds` is compared by content, so a caller may pass a fresh array every render.
 */
export function useGroups(noteIds: readonly number[] = []): UseGroups {
  const [groups, setGroups] = useState<CardGroup[]>([]);
  const [membership, setMembership] = useState<Map<number, string[]>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [token, setToken] = useState(0);
  const dialogs = useDialogs();

  const key = useMemo(() => [...new Set(noteIds)].sort((a, b) => a - b).join(","), [noteIds]);
  const bump = useCallback(() => setToken((n) => n + 1), []);
  useGroupsChanged(bump);

  useEffect(() => {
    let cancelled = false;
    void listGroups()
      .then((found) => {
        if (cancelled) return;
        setGroups(found);
        setLoaded(true);
        setError(null);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split(",").map(Number) : [];
    void groupMembership(ids)
      .then((found) => !cancelled && setMembership(found))
      .catch(() => !cancelled && setMembership(new Map()));
    return () => {
      cancelled = true;
    };
  }, [key, token]);

  const write = useCallback(async (act: () => Promise<unknown>) => {
    try {
      await act();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      announceGroupsChanged();
    }
  }, []);

  const actions = useMemo<GroupMenuActions>(
    () => ({
      add: (group, ids) => void write(() => addToGroup(group.id, ids)),
      remove: (group, ids) => void write(() => removeFromGroup(group.id, ids)),
      create: (ids) =>
        void (async () => {
          const name = await dialogs.prompt({
            title: "新しいグループ",
            label: "グループ名",
            submitLabel: ids.length ? "作成して追加" : "作成",
          });
          if (!name?.trim()) return;
          await write(() => createGroup(name.trim(), ids));
        })(),
    }),
    [dialogs, write],
  );

  return { groups, membership, actions, error, loaded };
}
